import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewRef
} from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import {CommonModule} from "@angular/common";
import {FormsModule} from "@angular/forms";
import {ToastService} from "../../../services/Toast.service";
import {NumeralService} from "../../../services/Numeral.service";
import {SwitchBoard} from "../../../services/SwitchBoard";
import {LogoService} from "../../../services/LogoService";
import {LoadingService} from "../../../services/LoadingService";
import {TOAST_TYPE} from "../../../constants/Tpiq.constants";
import {TaxPlanningService} from "../../../tpiq-services/tax-planning.service";
import {ExportService} from "../../../services/Export.service";
import {Session} from "../../../services/Session";
import {environment} from "../../../../environments/environment";
import {TpiqSelectDropdownComponent} from "../../../input-search/tpiq-select-dropdown/tpiq-select-dropdown.component";
import {FormatAmountPipe} from "../../../pipes/format-amount.pipe";
import {FroalaEditorModule} from "angular-froala-wysiwyg";
import {ModelBasedNumeral} from "../../../directives/model-numeral";
import {FoundationInit} from "../../../directives/foundation.directive";
import {TpiqCustomDatepicker} from "../../../directives/tpiq-custom-datepicker";
import { TaxEstimate, TaxEstimateProjection } from '../../../models/tax-estimate.model';
import { TaxEstimatesAmountPipe } from '../../../pipes/tax-estimates.pipe';
import { EstimateDetailedCalculationComponent } from './estimate-details/estimate-detailed-calculation.component';

declare const _: any;
declare const jQuery: any;
declare const moment: any;

@Component({
  selector: 'app-tax-estimates-inputs',
  standalone: true,
  imports: [CommonModule, FormsModule, TpiqSelectDropdownComponent, FormatAmountPipe, TaxEstimatesAmountPipe, FroalaEditorModule, ModelBasedNumeral, FoundationInit,
    TpiqCustomDatepicker, EstimateDetailedCalculationComponent],
  templateUrl: './tax-estimates-inputs.component.html',
  styleUrl: './tax-estimates-inputs.component.scss'
})
export class TaxEstimatesInputsComponent implements OnInit, OnDestroy {
  org: string = Session.getOrg();
  user: any = {};
  orgName: string;
  orgLogo: string;
  currentEnvironment: any;
  @Input() planYear: any;
  @Input() clientTaxplanData: any;
  @Input() bgColor: any;
  @Input() clientsList: any;
  @Input() strategiesList: any;
  @Input() associatedWithData: any;
  @Input() stateAssociatedWithData: any;
  @Input() allAssociatedData: any;
  @Input() clientData: any;
  @ViewChild('detailReport') detailReport;
  @Output() triggerForRefreshPlan: EventEmitter<any> = new EventEmitter<any>();

  taxEstimates: TaxEstimate = new TaxEstimate();

  filingStatusList: Array<any> = [
    {name: 'Single'},
    {name: 'MFJ'},
    {name: 'MFS'},
    {name: 'Head of Household'},
    {name: 'Estate'},
    {name: 'Trust'}
  ];
  taxProjectionsList: Array<any> = [
    {name: 'Federal 1040 Tax Projection', id: 'Federal 1040 Tax Projection'},
    {name: 'State Personal Tax Projection', id: 'State Personal Tax Projection'},
    {name: 'C-Corp Federal Tax Projection', id: 'C-Corp Federal Tax Projection'},
    {name: 'C-Corp State Tax Projection', id: 'C-Corp State Tax Projection'}
  ];
  currentTaxProjection = 'Federal 1040 Tax Projection';
  projectionsArray: Array<TaxEstimateProjection> = TaxEstimate.createDefaultProjections();

  showProjection: boolean = true;
  planTaxEstimatesData: any;
  hasData: boolean = false;
  exportedDate: any;
  backgroundColor: string;
  projectionsToPdf: Array<any> = [];
  editorOptions = {
    heightMin: 100,
    heightMax: 100,
    linkAlwaysBlank: true,
    toolbarButtons: ['bold', 'italic', 'underline', 'fontFamily', 'fontSize', 'color', 'insertLink', 'outdent', 'indent'],
    toolbarSticky: true,
    toolbarVisibleWithoutSelection: false,
    toolbarInline: true,
    fontFamilySelection: true,
    placeholderText: 'Firm Notes',
    charCounterCount: false,
    colorsText: ['#14B5F0', '#3574e3', '#DFCF6A', '#2D62BF', '#09A4DE', '#475E79', '#3474e3', '#102D79', '#434343'],
    events : {},
    key: environment.froalaKey,
  };
  estimateDetailReport: any;
  w2ValueChanged: boolean = false;
  estimatesPaidCganged: boolean = false;

  // Auto-save properties
  private saveSubject = new Subject<void>();
  private destroy$ = new Subject<void>();
  saveStatus: 'idle' | 'saving' | 'saved' | 'error' = 'idle';
  private lastSavedData: string = '';
  reveal: any = {
    showFormulaInfo: false,
    selectProjection: false
  };
  currentFormulaKey: any;
  projectionDropdownSettings = {
    singleSelection: false,
    idField: 'id',
    textField: 'name',
    selectAllText: 'Select All',
    unSelectAllText: 'UnSelect All',
    itemsShowLimit: 1,
    allowSearchFilter: true,
  };
  selectedProjectionsForPdf = [];

  constructor(
    private loadingService: LoadingService,
    private toastService: ToastService,
    private switchBoard: SwitchBoard,
    private logoService: LogoService,
    private taxPlanningService: TaxPlanningService,
    private numeralService: NumeralService,
    private changeDetectorRef: ChangeDetectorRef,
    private exportService: ExportService,
  ) {}

  ngOnInit(): void {
    const base = this;
    this.user = Session.getUser();
    this.orgName = this.user.org || '';
    this.orgLogo = this.logoService.getLoginLogoUrl();
    this.currentEnvironment = environment;
    this.exportedDate = moment(new Date()).format('MM/DD/YYYY');
    this.backgroundColor = this.bgColor ? this.bgColor : '#fff';
    this.getPlanTaxEstimatesData();

    // Setup debounced auto-save (4 second delay after last change)
    this.saveSubject.pipe(
      debounceTime(4000),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.autoSave();
    });
  }

  getPlanTaxEstimatesData() {
    this.loadingService.triggerLoadingEvent(true);
    this.taxPlanningService.getPlanTaxEstimates(this.org, this.user.id, this.clientTaxplanData.id)
      .subscribe(response => {
        this.loadingService.triggerLoadingEvent(false);
        if (response) {
          this.planTaxEstimatesData = response;

          try {
            const rawStr = response.detailReport;           // OCR snapshot (flat keys)
            const savedStr = response.detailReportSaved;    // user edits (structured patch)

            const rawParsed = rawStr ? JSON.parse(rawStr) : {};
            const savedParsed = (savedStr && savedStr.length) ? JSON.parse(savedStr) : null;

            // Normalize the OCR snapshot into the full structured shape.
            const rawNorm = this.normalizeDetailReport(rawParsed);

            // IMPORTANT: treat detailReportSaved as a PATCH.
            // Do NOT run it through normalizeDetailReport() (which fills missing fields with 0/defaults),
            // otherwise defaults overwrite real OCR values.
            const savedPatch = this.normalizeSavedDetailReport(savedParsed);

            // saved patch overrides OCR, but OCR provides the baseline
            this.estimateDetailReport = _.merge({}, rawNorm, savedPatch);
          } catch (e) {
            this.estimateDetailReport = {};
          }

          this.setUpTaxEstimateValues();
        }
        this.hasData = true;
      }, error => {
        this.loadingService.triggerLoadingEvent(false);
        this.toastService.pop(TOAST_TYPE.error, 'Failed to get tax estimates');
      });
  }

  setUpTaxEstimateValues() {
    const base = this;
    if (this.planTaxEstimatesData) {
      this.taxEstimates.id = this.planTaxEstimatesData.id;
      this.taxEstimates.planId = (this.planTaxEstimatesData.planId) ? this.planTaxEstimatesData.planId : this.clientTaxplanData.id;
      this.taxEstimates.planYear = (this.planTaxEstimatesData.planYear) ? this.planTaxEstimatesData.planYear : this.planYear;
      this.taxEstimates.currentYearTax = this.planTaxEstimatesData.currentYearTax;
      this.taxEstimates.currentYearAGI = this.planTaxEstimatesData.currentYearAGI;
      this.taxEstimates.priorYearTax = this.planTaxEstimatesData.priorYearTax;
      this.taxEstimates.priorYearAGI = this.planTaxEstimatesData.priorYearAGI;
      this.taxEstimates.filingStatus = this.planTaxEstimatesData.filingStatus;
      let firmNotes = (this.planTaxEstimatesData.notes) ? this.planTaxEstimatesData.notes : '';
      this.taxEstimates.notes = firmNotes;
      this.taxEstimates.status = this.planTaxEstimatesData.status;
      if (this.planTaxEstimatesData.projections && this.planTaxEstimatesData.projections.length > 0) {
        const projections = [];
        _.each(this.planTaxEstimatesData.projections, function (estimate) {
          let projection = JSON.parse(estimate.projectionData).projection;
          if (projection) {
            projection.id = estimate.id;
            base.setUpTodoIdForProjections(estimate, projection);
            projections.push(projection);
          }
        });
        this.projectionsArray = projections;
      }
    }
  }

  setUpTodoIdForProjections(estimate, projection) {
    if (estimate.lines && estimate.lines.length > 0) {
      _.each(estimate.lines, function (line) {
        if (line.quarter == 'Q1') {
          projection.quarter1['todoId'] = line.todoId;
          projection.quarter1['id'] = line.id;
        } else if (line.quarter == 'Q2') {
          projection.quarter2['todoId'] = line.todoId;
          projection.quarter2['id'] = line.id;
        } else if (line.quarter == 'Q3') {
          projection.quarter3['todoId'] = line.todoId;
          projection.quarter3['id'] = line.id;
        } else if (line.quarter == 'Q4') {
          projection.quarter4['todoId'] = line.todoId;
          projection.quarter4['id'] = line.id;
        } else if (line.quarter == 'Additional Payment1') {
          projection.addittionalPayment1['todoId'] = line.todoId;
          projection.addittionalPayment1['id'] = line.id;
        } else if (line.quarter == 'Additional Payment2') {
          projection.addittionalPayment2['todoId'] = line.todoId;
          projection.addittionalPayment2['id'] = line.id;
        } else if (line.quarter == 'Extension Payment') {
          projection.extensionPayment['todoId'] = line.todoId;
          projection.extensionPayment['id'] = line.id;
        }
      });
    }
  }

  ngOnDestroy(): void {
    // Trigger final auto-save before destroying
    this.autoSave();
    this.destroy$.next();
    this.destroy$.complete();
    this.saveSubject.complete();
    this.switchBoard.onToggleFeedback.next({
      display: false
    });
  }

  formatAmount(amount){
    return this.numeralService.format('$0,0', amount);
  }

  /**
   * Normalizes either:
   *  - the legacy OCR flat JSON (keys like us_1040_taxable_interest)
   *  - the structured Detailed Calculation JSON (incomeInfo/adjustmentsToIncomeInfo/otherDetailedInfo)
   * into the structured shape expected by EstimateDetailedCalculationComponent.
   */
  normalizeDetailReport(dr: any): any {
    const base = this.emptyDetailReportShape();

    if (!dr || typeof dr !== 'object') {
      return base;
    }

    // If already structured, merge and return.
    if (dr.incomeInfo || dr.adjustmentsToIncomeInfo || dr.otherDetailedInfo) {
      return _.merge({}, base, dr);
    }

    // Otherwise assume OCR flat payload.
    const flat = dr;

    // Income
    // Wages
    // Some OCR payloads provide split wages (taxpayer/spouse). Others only provide the combined 1040 line 1a.
    const w2Taxpayer = this.coerceNumber((flat as any).us_1040_wages_taxpayer);
    const w2Spouse = this.coerceNumber((flat as any).us_1040_wages_spouse);
    const w2Line1a = this.coerceNumber((flat as any).us_1040_line_1a_total_amount);

    // Prefer split wages when present; otherwise fall back to combined line 1a into taxpayer.
    base.incomeInfo.wagesTaxpayer.lastYearValue = (w2Taxpayer || w2Spouse) ? w2Taxpayer : w2Line1a;
    base.incomeInfo.wagesSpouse.lastYearValue = (w2Taxpayer || w2Spouse) ? w2Spouse : 0;

    base.incomeInfo.interest.lastYearValue = this.coerceNumber(flat.us_1040_taxable_interest);
    base.incomeInfo.dividents.lastYearValue = this.coerceNumber(flat.us_1040_ordinary_dividends);
    base.incomeInfo.businessIncome.lastYearValue = this.coerceNumber(flat.schedule_1_business_income_or_loss);
    base.incomeInfo.rentsAndLoyalties.lastYearValue = this.coerceNumber(flat.schedule_1_rental_real_estate_royalties);
    base.incomeInfo.k1Passthrough.lastYearValue = this.coerceNumber(flat.schedule_E_income_or_loss_from_partnerships_and_s_corporations);
    base.incomeInfo.stCapGn.lastYearValue = this.coerceNumber(flat.us_1040_capital_gain_or_loss);

    // Adjustments
    base.adjustmentsToIncomeInfo.selfEmployedTax.lastYearValue = this.coerceNumber(flat.schedule_1_self_employment_tax);
    base.adjustmentsToIncomeInfo.estimatedAdjustedGrossInc.lastYearValue = this.coerceNumber(flat.us_1040_adjusted_gross_income);

    // Other details
    base.otherDetailedInfo.standardDeduction.lastYearValue = this.coerceNumber(flat.us_1040_line_12_standard_deduction_or_itemized_deductions);
    base.otherDetailedInfo.otherTaxes.lastYearValue = this.coerceNumber(flat.schedule_2_net_investment_income_tax);
    base.otherDetailedInfo.qualifiedBusinessIncDed.lastYearValue = this.coerceNumber(flat.us_1040_qualified_income_deduction);
    base.otherDetailedInfo.finalTaxableIncome.lastYearValue = this.coerceNumber(flat.us_1040_line_15_taxable_income);
    base.otherDetailedInfo.totalTax.lastYearValue = this.coerceNumber(flat.us_1040_total_tax);
    base.otherDetailedInfo.estimatedTaxPayments.lastYearValue = this.coerceNumber(flat.us_1040_total_payments);
    base.otherDetailedInfo.underPaymentPenalties.lastYearValue = this.coerceNumber(flat.us_1040_line_38_estimated_tax_penalty);
    base.otherDetailedInfo.effectiveTaxRate.lastYearValue = this.coerceNumber(flat.us_1040_tax_rate);

    return base;
  }

  /**
   * detailReportSaved should be treated as a PATCH over the OCR-derived baseline.
   * If it's already structured (incomeInfo/adjustmentsToIncomeInfo/otherDetailedInfo), return as-is.
   * If it's a legacy flat payload, normalize it.
   */
  private normalizeSavedDetailReport(dr: any): any {
    if (!dr || typeof dr !== 'object') return {};
  
    // Empty object = no patch (critical)
    if (Object.keys(dr).length === 0) return {};
  
    // Structured = treat as patch (do NOT fill defaults)
    if (dr.incomeInfo || dr.adjustmentsToIncomeInfo || dr.otherDetailedInfo) {
      return dr;
    }
  
    // Otherwise assume legacy flat JSON and normalize
    return this.normalizeDetailReport(dr);
  }

  private coerceNumber(v: any): number {
    if (v === null || v === undefined) return 0;
    if (typeof v === 'number') return isFinite(v) ? v : 0;
    if (typeof v === 'string') {
      const s = v.trim();
      if (!s) return 0;
      // strip $, commas, and whitespace
      const cleaned = s.replace(/[$,\s]/g, '');
      const n = Number(cleaned);
      return isFinite(n) ? n : 0;
    }
    return 0;
  }

  private emptyDetailReportShape(): any {
    return {
      incomeInfo: {
        wagesTaxpayer: { lastYearValue: 0, currentYearvalue: 0, notes: '' },
        wagesSpouse: { lastYearValue: 0, currentYearvalue: 0, notes: '' },
        interest: { lastYearValue: 0, currentYearvalue: 0, notes: '' },
        dividents: { lastYearValue: 0, currentYearvalue: 0, notes: '' },
        businessIncome: { lastYearValue: 0, currentYearvalue: 0, notes: '' },
        rentsAndLoyalties: { lastYearValue: 0, currentYearvalue: 0, notes: '' },
        k1Passthrough: { lastYearValue: 0, currentYearvalue: 0, notes: '' },
        stCapGn: { lastYearValue: 0, currentYearvalue: 0, notes: '' },
        ltCapGn: { lastYearValue: 0, currentYearvalue: 0, notes: '' },
        otherGainsOrLosses: { lastYearValue: 0, currentYearvalue: 0, notes: '' },
        otherIncome: { lastYearValue: 0, currentYearvalue: 0, notes: '' },
        totalIncome: { lastYearValue: 0, currentYearvalue: 0, notes: '' },
      },
      adjustmentsToIncomeInfo: {
        hsa: { lastYearValue: 0, currentYearvalue: 0, notes: '' },
        retirementBySpouse: { lastYearValue: 0, currentYearvalue: 0, notes: '' },
        selfEmployedTax: { lastYearValue: 0, currentYearvalue: 0, notes: '' },
        selfEmployedHealthInsDed: { lastYearValue: 0, currentYearvalue: 0, notes: '' },
        otherAdjustments: { lastYearValue: 0, currentYearvalue: 0, notes: '' },
        estimatedAdjustedGrossInc: { lastYearValue: 0, currentYearvalue: 0, notes: '' },
      },
      otherDetailedInfo: {
        standardDeduction: { lastYearValue: 0, currentYearvalue: 0, notes: '' },
        taxableIncBeforeQBI: { lastYearValue: 0, currentYearvalue: 0, notes: '' },
        qualifiedBusinessIncDed: { lastYearValue: 0, currentYearvalue: 0, notes: '' },
        finalTaxableIncome: { lastYearValue: 0, currentYearvalue: 0, notes: '' },
        otherTaxes: { lastYearValue: 0, currentYearvalue: 0, notes: '' },
        taxOrdinary: { lastYearValue: 0, currentYearvalue: 0, notes: '' },
        underPaymentPenalties: { lastYearValue: 0, currentYearvalue: 0, notes: '' },
        totalTax: { lastYearValue: 0, currentYearvalue: 0, notes: '' },
        estimatedTaxPayments: { lastYearValue: 0, currentYearvalue: 0, notes: '' },
        effectiveTaxRate: { lastYearValue: 0, currentYearvalue: 0, notes: '' },
      }
    };
  }

  onFilingSelect(status) {
    if (!_.isEmpty(status)) {
      this.taxEstimates.filingStatus = status.name;
      this.markDirty();
      if (this.currentTaxProjection == 'Federal 1040 Tax Projection') {
        this.calculateSafeHarbor();
      } else if (this.currentTaxProjection == 'State Personal Tax Projection') {
        this.calculateMinAmountDueToIRS();
      } else {
        this.calculateRemainderDueOrRefund();
      }
    }
  }

  calculateSafeHarbor() {
    if (this.taxEstimates.priorYearTax) {
      if (this.currentTaxProjection == 'Federal 1040 Tax Projection') {
        if (this.taxEstimates.filingStatus == 'MFJ' && this.taxEstimates.priorYearAGI > 150000) {
          this.projectionsArray[0].safeHarbor = (this.taxEstimates.priorYearTax * (110 / 100));
        } else if (this.taxEstimates.filingStatus == 'MFJ' && this.taxEstimates.priorYearAGI <= 150000) {
          this.projectionsArray[0].safeHarbor = this.taxEstimates.priorYearTax;
        } else if ((this.taxEstimates.filingStatus == 'Single' || this.taxEstimates.filingStatus == 'Head of Household' || this.taxEstimates.filingStatus == 'MFS') && this.taxEstimates.priorYearAGI > 75000) {
          this.projectionsArray[0].safeHarbor = (this.taxEstimates.priorYearTax * (110 / 100));
        } else if ((this.taxEstimates.filingStatus == 'Single' || this.taxEstimates.filingStatus == 'Head of Household' || this.taxEstimates.filingStatus == 'MFS') && this.taxEstimates.priorYearAGI <= 75000) {
          this.projectionsArray[0].safeHarbor = this.taxEstimates.priorYearTax;
        }
        this.calculateTaxDue();
      }
    }
  }

  calculateTaxDue() {
    if (this.taxEstimates.currentYearTax) {
      if (this.currentTaxProjection == 'Federal 1040 Tax Projection') {
        this.projectionsArray[0].currentYearExpectedMinTaxDue = (this.taxEstimates.currentYearTax * (90 / 100));
        this.calculateMinAmountDueToIRS();
      }
    }
  }

  dateChanged(date, projection, keyName, type) {
    if (type == 'paid date') {
      projection[keyName].paidDate = date;
    } else if (type == 'due date'){
      projection[keyName].dueDate = date;
    }
    this.markDirty();
  }

  onTaxProjectionSelect(projection) {
    this.showProjection = false;
    this.currentTaxProjection = projection.name;
    setTimeout(() => {
      this.showProjection = true;
    });
  }

  calculateRecommendedEstimatesTotal(projection) {
    projection.recommendedEstimates = 0;
    projection.recommendedEstimates = (projection.quarter1.recommended) ? projection.recommendedEstimates + projection.quarter1.recommended : projection.recommendedEstimates;
    projection.recommendedEstimates = (projection.quarter2.recommended) ? projection.recommendedEstimates + projection.quarter2.recommended : projection.recommendedEstimates;
    projection.recommendedEstimates = (projection.quarter3.recommended) ? projection.recommendedEstimates + projection.quarter3.recommended : projection.recommendedEstimates;
    projection.recommendedEstimates = (projection.quarter4.recommended) ? projection.recommendedEstimates + projection.quarter4.recommended : projection.recommendedEstimates;
    projection.recommendedEstimates = (projection.addittionalPayment1.recommended) ? projection.recommendedEstimates + projection.addittionalPayment1.recommended : projection.recommendedEstimates;
    projection.recommendedEstimates = (projection.addittionalPayment2.recommended) ? projection.recommendedEstimates + projection.addittionalPayment2.recommended : projection.recommendedEstimates;
    projection.recommendedEstimates = (projection.extensionPayment.recommended) ? projection.recommendedEstimates + projection.extensionPayment.recommended : projection.recommendedEstimates;

    this.markDirty();
    this.calculateRemainderDueOrRefund();
  }

  calculatePaidEstimatesTotal(projection) {
    projection.paidEstimates = 0;
    projection.paidEstimates = (projection.quarter1.paid) ? projection.paidEstimates + projection.quarter1.paid : projection.paidEstimates;
    projection.paidEstimates = (projection.quarter2.paid) ? projection.paidEstimates + projection.quarter2.paid : projection.paidEstimates;
    projection.paidEstimates = (projection.quarter3.paid) ? projection.paidEstimates + projection.quarter3.paid : projection.paidEstimates;
    projection.paidEstimates = (projection.quarter4.paid) ? projection.paidEstimates + projection.quarter4.paid : projection.paidEstimates;
    projection.paidEstimates = (projection.addittionalPayment1.paid) ? projection.paidEstimates + projection.addittionalPayment1.paid : projection.paidEstimates;
    projection.paidEstimates = (projection.addittionalPayment2.paid) ? projection.paidEstimates + projection.addittionalPayment2.paid : projection.paidEstimates;
    projection.paidEstimates = (projection.extensionPayment.paid) ? projection.paidEstimates + projection.extensionPayment.paid : projection.paidEstimates;

    this.markDirty();
    this.estimatesPaidCganged = !this.estimatesPaidCganged;
    this.calculateRemainderDueOrRefund();
  }

  calculateRemainderDueOrRefund(isW2Changed?) {
    if (isW2Changed) {
      this.setUpW2ValueChange();
    }
    if (this.currentTaxProjection == 'Federal 1040 Tax Projection') {
      const minAmountDueToIrs = (this.projectionsArray[0].minimumAmountDueToIRS) ? this.projectionsArray[0].minimumAmountDueToIRS : 0;
      const overPaymentAppliedFromPY = (this.projectionsArray[0].overPaymentAppliedForPY) ? this.projectionsArray[0].overPaymentAppliedForPY : 0;
      const estimatedW2WithHolding = (this.projectionsArray[0].estimatedW2WithHolding) ? this.projectionsArray[0].estimatedW2WithHolding : 0;
      const estimatedW2WithHoldingSpouse = (this.projectionsArray[0].estimatedW2WithHoldingSpouse) ? this.projectionsArray[0].estimatedW2WithHoldingSpouse : 0;
      const taxEstimatesTotalRecommended = (this.projectionsArray[0].recommendedEstimates) ? this.projectionsArray[0].recommendedEstimates : 0;
      this.projectionsArray[0].remainderDueRefund = minAmountDueToIrs - overPaymentAppliedFromPY - estimatedW2WithHolding - estimatedW2WithHoldingSpouse - taxEstimatesTotalRecommended;
      this.projectionsArray[0].isRemainderDueOrRefundNegative = (this.projectionsArray[0].remainderDueRefund < 0) ? true : false;
    } else if (this.currentTaxProjection == 'State Personal Tax Projection') {
      const minAmountDueToIrs = (this.projectionsArray[1].minimumAmountDueToIRS) ? this.projectionsArray[1].minimumAmountDueToIRS : 0;
      const overPaymentAppliedFromPY = (this.projectionsArray[1].overPaymentAppliedForPY) ? this.projectionsArray[1].overPaymentAppliedForPY : 0;
      const estimatedW2WithHolding = (this.projectionsArray[1].estimatedW2WithHolding) ? this.projectionsArray[1].estimatedW2WithHolding : 0;
      const estimatedW2WithHoldingSpouse = (this.projectionsArray[1].estimatedW2WithHoldingSpouse) ? this.projectionsArray[1].estimatedW2WithHoldingSpouse : 0;
      const taxEstimatesTotalRecommended = (this.projectionsArray[1].recommendedEstimates) ? this.projectionsArray[1].recommendedEstimates : 0;
      this.projectionsArray[1].remainderDueRefund = minAmountDueToIrs - overPaymentAppliedFromPY - estimatedW2WithHolding - estimatedW2WithHoldingSpouse - taxEstimatesTotalRecommended;
      this.projectionsArray[1].isRemainderDueOrRefundNegative = (this.projectionsArray[1].remainderDueRefund < 0) ? true : false;
    } else if (this.currentTaxProjection == 'C-Corp Federal Tax Projection') {
      const curYearExpectedTaxDue = (this.projectionsArray[2].currentYearExpectedMinTaxDue) ? this.projectionsArray[2].currentYearExpectedMinTaxDue : 0;
      const overPaymentAppliedFromPY = (this.projectionsArray[2].overPaymentAppliedForPY) ? this.projectionsArray[2].overPaymentAppliedForPY : 0;
      const taxEstimatesTotalRecommended = (this.projectionsArray[2].recommendedEstimates) ? this.projectionsArray[2].recommendedEstimates : 0;
      this.projectionsArray[2].remainderDueRefund = curYearExpectedTaxDue - overPaymentAppliedFromPY -  taxEstimatesTotalRecommended;
      this.projectionsArray[2].isRemainderDueOrRefundNegative = (this.projectionsArray[2].remainderDueRefund < 0) ? true : false;
    } else if (this.currentTaxProjection == 'C-Corp State Tax Projection') {
      const curYearExpectedTaxDue = (this.projectionsArray[3].currentYearExpectedMinTaxDue) ? this.projectionsArray[3].currentYearExpectedMinTaxDue : 0;
      const overPaymentAppliedFromPY = (this.projectionsArray[3].overPaymentAppliedForPY) ? this.projectionsArray[3].overPaymentAppliedForPY : 0;
      const taxEstimatesTotalRecommended = (this.projectionsArray[3].recommendedEstimates) ? this.projectionsArray[3].recommendedEstimates : 0;
      this.projectionsArray[3].remainderDueRefund = curYearExpectedTaxDue - overPaymentAppliedFromPY -  taxEstimatesTotalRecommended;
      this.projectionsArray[3].isRemainderDueOrRefundNegative = (this.projectionsArray[3].remainderDueRefund < 0) ? true : false;
    }
  }

  calculateMinAmountDueToIRS() {
    if (this.currentTaxProjection == 'Federal 1040 Tax Projection') {
      if (this.projectionsArray[0].safeHarbor && this.projectionsArray[0].currentYearExpectedMinTaxDue) {
        this.projectionsArray[0].minimumAmountDueToIRS = (this.projectionsArray[0].safeHarbor > this.projectionsArray[0].currentYearExpectedMinTaxDue) ?
          this.projectionsArray[0].currentYearExpectedMinTaxDue : this.projectionsArray[0].safeHarbor;
      } else {
        this.projectionsArray[0].minimumAmountDueToIRS = 0;
      }
    }else if (this.currentTaxProjection == 'State Personal Tax Projection') {
      if (this.projectionsArray[1].priorYearTaxDue && this.projectionsArray[1].currentYearExpectedMinTaxDue) {
        this.projectionsArray[1].minimumAmountDueToIRS = (this.projectionsArray[1].priorYearTaxDue > this.projectionsArray[1].currentYearExpectedMinTaxDue) ?
          this.projectionsArray[1].currentYearExpectedMinTaxDue : this.projectionsArray[1].priorYearTaxDue;
      } else {
        this.projectionsArray[1].minimumAmountDueToIRS = 0;
      }
    }
    this.calculateRemainderDueOrRefund();
  }

  saveTaxProjection() {
    const data = this.buildSavePayload();
    this.loadingService.triggerLoadingEvent(true);
    this.taxPlanningService.savePlanTaxEstimates(this.org, this.user.id, this.clientTaxplanData.id, data)
      .subscribe(response => {
        this.loadingService.triggerLoadingEvent(false);
        this.toastService.pop(TOAST_TYPE.success, 'Saved Successfully');
        // Update lastSavedData to prevent duplicate auto-save
        this.lastSavedData = JSON.stringify(data);
        this.saveStatus = 'saved';
        if (response) {
          this.planTaxEstimatesData = response;

          try {
            const rawStr = response.detailReport;           // OCR snapshot (flat keys)
            const savedStr = response.detailReportSaved;    // user edits (structured)

            const rawParsed = rawStr ? JSON.parse(rawStr) : {};
            const savedParsed = (savedStr && savedStr.length) ? JSON.parse(savedStr) : null;

            const rawNorm = this.normalizeDetailReport(rawParsed);
            const savedNorm = this.normalizeDetailReport(savedParsed);

            // saved overrides OCR, but OCR provides the baseline
            this.estimateDetailReport = _.merge({}, rawNorm, savedNorm);
          } catch (e) {
            this.estimateDetailReport = {};
          }

          this.setUpTaxEstimateValues();
        }
      }, error => {
        this.loadingService.triggerLoadingEvent(false);
        this.saveStatus = 'error';
        try {
          const message = error.message || 'Failed to save tax estimates';
          this.toastService.pop(TOAST_TYPE.error, message);
        } catch (err) {
          this.toastService.pop(TOAST_TYPE.error, 'Failed to save tax estimates');
        }
      });
  }

  // Auto-save methods
  markDirty() {
    this.saveStatus = 'idle';
    this.saveSubject.next();
  }

  autoSave() {
    if (!this.hasData || !this.clientTaxplanData?.id) {
      return;
    }

    const data = this.buildSavePayload();
    const currentDataString = JSON.stringify(data);

    // Skip if no changes since last save
    if (currentDataString === this.lastSavedData) {
      return;
    }

    this.saveStatus = 'saving';
    this.taxPlanningService.savePlanTaxEstimates(this.org, this.user.id, this.clientTaxplanData.id, data)
      .subscribe(
        response => {
          this.lastSavedData = currentDataString;
          this.saveStatus = 'saved';
          if (response) {
            this.planTaxEstimatesData = response;
          }
        },
        error => {
          this.saveStatus = 'error';
        }
      );
  }

  private buildSavePayload(): any {
    let data: any = _.cloneDeep(this.taxEstimates);
    data.planId = this.clientTaxplanData.id;
    data.planYear = this.planYear;
    data['projections'] = this.getFormatedDataForProjections();

    // Prefer the live values from the Detailed Calculation child component (if present)
    const liveDetailReport = (this.detailReport && typeof this.detailReport.buildDetailReportPayload === 'function')
      ? this.detailReport.buildDetailReportPayload()
      : null;

    const hasLive =
      !!liveDetailReport &&
      typeof liveDetailReport === 'object' &&
      Object.keys(liveDetailReport).length > 0;

    const reportToSave = hasLive ? liveDetailReport : (this.estimateDetailReport || {});
    data.detailReportSaved = JSON.stringify(reportToSave);

    return data;
  }

  getFormatedDataForProjections() {
    const base = this;
    let data = [];
    let projectionsData = _.cloneDeep(this.projectionsArray);
    if (projectionsData && projectionsData.length > 0) {
      _.each(projectionsData, function (projection) {
        projection.estimateId = base.taxEstimates.id;
        projection['projectionData'] = JSON.stringify({projection: projection});
        const lines = [];
        lines.push({id: projection.quarter1.id, projectionId: projection.id, quarter: 'Q1', recommended: projection.quarter1.recommended, paid: projection.quarter1.paid,
          dueDate: (projection.quarter1.dueDate) ? projection.quarter1.dueDate : '', paidDate: projection.quarter1.paidDate, notes: projection.quarter1.notes, todoId: projection.quarter1.todoId});

        lines.push({id: projection.quarter2.id, projectionId: projection.id, quarter: 'Q2', recommended: projection.quarter2.recommended, paid: projection.quarter2.paid,
          dueDate: (projection.quarter2.dueDate) ? projection.quarter2.dueDate : '', paidDate: projection.quarter2.paidDate, notes: projection.quarter2.notes, todoId: projection.quarter2.todoId});

        lines.push({id: projection.quarter3.id, projectionId: projection.id, quarter: 'Q3', recommended: projection.quarter3.recommended, paid: projection.quarter3.paid,
          dueDate: (projection.quarter3.dueDate) ? projection.quarter3.dueDate : '', paidDate: projection.quarter3.paidDate, notes: projection.quarter3.notes, todoId: projection.quarter3.todoId});

        lines.push({id: projection.quarter4.id, projectionId: projection.id, quarter: 'Q4', recommended: projection.quarter4.recommended, paid: projection.quarter4.paid,
          dueDate: (projection.quarter4.dueDate) ? projection.quarter4.dueDate : '', paidDate: projection.quarter4.paidDate, notes: projection.quarter4.notes, todoId: projection.quarter4.todoId});

        lines.push({id: projection.addittionalPayment1.id, projectionId: projection.id, quarter: 'Additional Payment1', recommended: projection.addittionalPayment1.recommended, paid: projection.addittionalPayment1.paid,
          dueDate: (projection.addittionalPayment1.dueDate) ? projection.addittionalPayment1.dueDate : '', paidDate: projection.addittionalPayment1.paidDate, notes: projection.addittionalPayment1.notes, todoId: projection.addittionalPayment1.todoId});

        lines.push({id: projection.addittionalPayment2.id, projectionId: projection.id, quarter: 'Additional Payment2', recommended: projection.addittionalPayment2.recommended, paid: projection.addittionalPayment2.paid,
          dueDate: (projection.addittionalPayment2.dueDate) ? projection.addittionalPayment2.dueDate : '', paidDate: projection.addittionalPayment2.paidDate, notes: projection.addittionalPayment2.notes, todoId: projection.addittionalPayment2.todoId});

        lines.push({id: projection.extensionPayment.id, projectionId: projection.id, quarter: 'Extension Payment', recommended: projection.extensionPayment.recommended, paid: projection.extensionPayment.paid,
          dueDate: (projection.extensionPayment.dueDate) ? projection.extensionPayment.dueDate : '', paidDate: projection.extensionPayment.paidDate, notes: projection.extensionPayment.notes, todoId: projection.extensionPayment.todoId});

        projection['lines'] = lines;
      });
    }
    return projectionsData;
  }

  publishProjection(type) {
    this.loadingService.triggerLoadingEvent(true);
    this.taxPlanningService.publishPlanTaxEstimates(this.org, this.user.id, this.taxEstimates.id, type)
      .subscribe(response => {
        this.loadingService.triggerLoadingEvent(false);
        this.taxEstimates.status = type;
        let message;
        if (type == 'publish') {
          message = 'Published successfully';
        } else if (type == 'unpublish') {
          message = 'Unpublished Successfully';
        }
        this.toastService.pop(TOAST_TYPE.success, message);
      }, error => {
        this.loadingService.triggerLoadingEvent(false);
        let message;
        if (type == 'publish') {
          message = 'Failed to publish tax estimates';
        } else if (type == 'unpublish') {
          message = 'Failed to unpublish tax estimates';
        }
        this.toastService.pop(TOAST_TYPE.error, message);
      });
  }

  projectionToPdf() {
    this.reveal.selectProjection = true;
  }

  exportToPdf() {
    const pdfReq = this.getPDFRequestObject();
    const url = '/TaxPlanningServices/orgs/' + this.org + '/users/' + this.user.id + '/export/custom-pdf';
    this.loadingService.triggerLoadingEvent(true);
    this.exportService.exportReportIntoFile(url, pdfReq)
      .subscribe(data => {
        this.loadingService.triggerLoadingEvent(false);
        const blob = new Blob([data], {type: 'application/pdf'});
        const link = jQuery('<a></a>');
        link[0].href = URL.createObjectURL(blob);
        link[0].download = `Tax Estimates.pdf`;
        link[0].click();
      }, error => {
        this.loadingService.triggerLoadingEvent(false);
        this.toastService.pop(TOAST_TYPE.error, 'Failed To Export Into PDF');
      });
  }

  validateProjections() {
    const base = this;
    const showProjections = [];
    if (this.projectionsArray && this.projectionsArray.length > 0) {
      _.each(this.projectionsArray, function (projection) {
        if (projection.projectionName == 'Federal 1040 Tax Projection') {
          if (projection.overPaymentAppliedForPY && projection.estimatedW2WithHolding && base.validateQuarters(projection)) {
            showProjections.push(projection);
          }
        } else if (projection.projectionName == 'State Personal Tax Projection') {
          if (projection.priorYearTaxDue && projection.currentYearExpectedMinTaxDue && projection.overPaymentAppliedForPY && projection.estimatedW2WithHolding && base.validateQuarters(projection)) {
            showProjections.push(projection);
          }
        } else if (projection.projectionName == 'C-Corp Federal Tax Projection') {
          if (projection.currentYearExpectedMinTaxDue && projection.overPaymentAppliedForPY && base.validateQuarters(projection)) {
            showProjections.push(projection);
          }
        } else if (projection.projectionName == 'C-Corp State Tax Projection') {
          if (projection.currentYearExpectedMinTaxDue && projection.overPaymentAppliedForPY && base.validateQuarters(projection)) {
            showProjections.push(projection);
          }
        }
      });
    }
    this.projectionsToPdf = showProjections;
  }

  validateQuarters(projection) {
    let validProjection = false;
    if (projection.quarter1.recommended && projection.quarter1.paid) {
      validProjection = true;
    } else if (projection.quarter2.recommended && projection.quarter2.paid) {
      validProjection = true;
    } else if (projection.quarter3.recommended && projection.quarter3.paid) {
      validProjection = true;
    } else if (projection.quarter4.recommended && projection.quarter4.paid) {
      validProjection = true;
    } else if (projection.addittionalPayment1.recommended && projection.addittionalPayment1.paid) {
      validProjection = true;
    } else if (projection.addittionalPayment2.recommended && projection.addittionalPayment2.paid) {
      validProjection = true;
    } else if (projection.extensionPayment.recommended && projection.extensionPayment.paid) {
      validProjection = true;
    }
    return validProjection;
  }

  getPDFRequestObject(){
    let htmlTemps = [];
    let estimateInfoHtml = this.getEstimateTemplate();
    estimateInfoHtml = this.getReplacedSpacialCharactersString(estimateInfoHtml);
    htmlTemps.push(estimateInfoHtml);
    const pdfReq = {
      version: '1.1',
      htmlTemplates: htmlTemps,
      footer: moment(new Date()).format('MMMM DD, YYYY HH:mm a')
    };
    return pdfReq;
  }

  getReplacedSpacialCharactersString(inputString) {
    if (inputString) {
      inputString = inputString.replaceAll("<br>", "<br/>");
      inputString = inputString.replaceAll('“', '"');
      inputString = inputString.replaceAll('”', '"');
      inputString = inputString.replaceAll('‘', "'");
      inputString = inputString.replaceAll('’', "'");
      inputString = inputString.replace(/(<img("[^"]*"|[^/">])*)>/gi, '$1/>');
    }
    return inputString;
  }

  getStyleString() {
    let styleString = '';
    const styleHtml = jQuery('style').clone();
    if (styleHtml && styleHtml.length >= 2){
      for (let i = 1; i < styleHtml.length; i++){
        styleString += styleHtml[i].outerHTML;
      }
    }
    return styleString;
  }

  getEstimateTemplate() {
    const styleString = this.getStyleString();
    const img = `<img width="50%" src="${this.orgLogo}"/>`;
    let html = jQuery('<div>').append(styleString).append(jQuery('#tax-estimate-projection').clone()).html();
    html = html.replace('````', img);
    return html;
  }

  getDetailReportTemplate() {
    const styleString = this.getStyleString();
    let html = jQuery('<div>').append(styleString).append(jQuery('#estimate-detail-report').clone()).html();
    return html;
  }

  exportToExcel() {
    let urlString = (this.currentEnvironment.production) ? 'https://services.taxplaniq.com' : 'https://dev-services.taxplaniq.com';
    const data: any = {};
    data.planId = this.clientTaxplanData.id;
    data.reportName = 'tax_estimates';
    data.orgId = this.org;
    data.userId = this.user.id;
    data.orgName = this.orgName;
    data.logoUrl = this.orgLogo;
    data.clientName = this.clientTaxplanData.userName;
    data.planName = this.clientTaxplanData.name;
    data.year = this.planYear;
    this.loadingService.triggerLoadingEvent(true);
    this.taxPlanningService.exportROIExcel(this.org, this.user.id, data)
      .subscribe(response => {
        this.loadingService.triggerLoadingEvent(false);
        const blob = new Blob([response], {type: 'application/vnd.ms-excel'});
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = `Tax Planning.xls`;
        link.click();
      }, error => {
        this.loadingService.triggerLoadingEvent(false);
        this.toastService.pop(TOAST_TYPE.error, 'Failed To Export Into Excel');
      });
  }

  setUpW2ValueChange() {
    this.w2ValueChanged = !this.w2ValueChanged;
  }

  hideReveal(key){
    this.selectedProjectionsForPdf = [];
    this.reveal[key] = false;
  }

  showCalculationInfo(key) {
    this.reveal.showFormulaInfo = true;
    this.currentFormulaKey = key;
  }

  triggerOnRefreshStrategies() {
    this.triggerForRefreshPlan.emit();
  }

  onProjectionFilterSelect(projection: any, check) {
    if (check) {
      this.selectedProjectionsForPdf.push(projection);
      this.selectedProjectionsForPdf = _.uniqBy(this.selectedProjectionsForPdf, 'id');
    } else {
      this.selectedProjectionsForPdf = _.without(this.selectedProjectionsForPdf, projection.id);
    }
  }

  onAllProjectionFilterSelect(projections: any, check) {
    if (check) {
      this.selectedProjectionsForPdf = _.map(projections, 'id');
    } else {
      this.selectedProjectionsForPdf = [];
    }
  }

  exportSelectedProjections() {
    this.projectionsToPdf = [];
    _.each(this.selectedProjectionsForPdf, (projection) => {
      const projectionObj = _.find(this.projectionsArray, {projectionName: projection.name});
      if (projectionObj) {
        this.projectionsToPdf.push(projectionObj);
      }
    });
    this.invokeChangeDetection();
    if (this.projectionsToPdf.length > 0) {
      setTimeout(() => {
        this.hideReveal('selectProjection');
        this.exportToPdf();
      });
    } else {
      this.toastService.pop(TOAST_TYPE.warning, 'Please select projection to export');
    }
  }

  invokeChangeDetection(): void {
    if (!(this.changeDetectorRef as ViewRef).destroyed) {
      this.changeDetectorRef.detectChanges();
    }
  }

  private parseNumberLike(value: any): number {
    if (value === null || value === undefined) return 0;
    if (typeof value === 'number') return isFinite(value) ? value : 0;
  
    const s = String(value).trim();
    if (!s) return 0;
  
    // remove currency symbols, commas, and spaces
    const cleaned = s.replace(/[$,\s]/g, '');
    const n = Number(cleaned);
    return isFinite(n) ? n : 0;
  }

}
